import type Geometry from 'ol/geom/Geometry';
import type { LayerModel, OperationalAttributeField, OperationalSubarea } from '../core/types';
import { hasThematicValue } from './classification';
import { createOperationalField } from './operationalFields';
import { createOperationalSubarea, createRemainingSubareaPreview, createSubareaPreview, type SubareaParent, type SubareaPreview } from './operationalSubareas';

/**
 * «Dividir estado de Suerte»: orchestration only. The geometry is the existing one (#35 createSubareaPreview +
 * createRemainingSubareaPreview) and the map is the existing mixed classification (#36). Everything is PLANNED here as
 * pure values: nothing is stored until the caller commits the whole plan in a single store update, so a failure at any
 * step leaves no field, no subarea and no classification change behind. The Maestro is never touched.
 */

/** Same rule the project-field system uses to block duplicates: trimmed, case-insensitive, no fuzzy matching. */
const sameName = (a: string, b: string): boolean => a.trim().toLocaleLowerCase() === b.trim().toLocaleLowerCase();
const MAX_SUGGESTED = 30;

/** Suerte-level text fields that can be split (project or Excel fields; the Maestro itself is never written). */
export const splittableSuerteFields = (fields: readonly OperationalAttributeField[]): readonly OperationalAttributeField[] =>
  fields.filter(({ descriptor }) => descriptor.level === 'suerte' && descriptor.type === 'string');

/** Text field of level Área parcial named like the Suerte field: reused instead of duplicated. */
export const matchingPartialField = (fields: readonly OperationalAttributeField[], name: string): OperationalAttributeField | undefined =>
  fields.find(({ descriptor }) => descriptor.level === 'subarea' && descriptor.type === 'string' && sameName(descriptor.name, name));

/** Values to offer for both states: the field's own suggestions and the values already in use (text only, ≤ 30). */
export function splitValueSuggestions(fields: readonly OperationalAttributeField[], baseFieldId: string | null, baseName: string): readonly string[] {
  const base = baseFieldId ? fields.find(({ descriptor }) => descriptor.id === baseFieldId) : undefined;
  const partial = matchingPartialField(fields, base?.descriptor.name ?? baseName);
  const values = [base, partial].flatMap((field) => field ? [...(field.descriptor.suggestedValues ?? []), ...Object.values(field.valuesByEntityId).filter(hasThematicValue).map(String)] : []);
  const unique = [...new Set(values.map((value) => value.trim()).filter(Boolean))];
  return unique.length <= MAX_SUGGESTED ? unique.sort((a, b) => a.localeCompare(b, 'es', { numeric: true })) : [];
}

/** V1 safety: any Área parcial already inside the Suerte (of the Suerte or of one of its Tablones) blocks the guided split. */
export function suerteSplitBlocker(model: LayerModel, suerteId: string, subareas: readonly OperationalSubarea[]): string | null {
  if (!model.suertes.has(suerteId)) return 'No se encontró la Suerte seleccionada.';
  return subareas.some((subarea) => subarea.suerteId === suerteId || (subarea.parentLevel === 'suerte' && subarea.parentId === suerteId))
    ? 'Esta Suerte ya contiene Áreas parciales. Usa las herramientas avanzadas para editarla o elimina las áreas existentes antes de crear una división guiada.'
    : null;
}

export type SplitBaseField = { readonly fieldId: string } | { readonly newName: string };

export interface SuerteSplitInput {
  readonly model: LayerModel;
  readonly subareas: readonly OperationalSubarea[];
  readonly fields: readonly OperationalAttributeField[];
  readonly suerteId: string;
  readonly base: SplitBaseField;
  readonly drawnValue: string;
  readonly restValue: string;
  readonly drawn: Geometry;
  /** Deterministic ids for tests; random UUIDs otherwise. */
  readonly ids?: { readonly drawn: string; readonly rest: string };
}

export interface SuerteSplitPlan {
  readonly parent: SubareaParent;
  readonly baseFieldId: string;
  readonly baseFieldName: string;
  readonly partialFieldId: string;
  readonly createdBaseField: boolean;
  readonly createdPartialField: boolean;
  readonly drawnValue: string;
  readonly restValue: string;
  readonly drawnPreview: SubareaPreview;
  readonly restPreview: SubareaPreview;
  readonly drawnSubarea: OperationalSubarea;
  readonly restSubarea: OperationalSubarea;
  /** Complete next lists: the caller stores them as they are. */
  readonly subareas: readonly OperationalSubarea[];
  readonly fields: readonly OperationalAttributeField[];
  /** References the plan was computed against: committing over a different state must re-plan. */
  readonly basis: { readonly model: LayerModel; readonly subareas: readonly OperationalSubarea[]; readonly fields: readonly OperationalAttributeField[] };
}

/** Validates the two states before anything is drawn. */
export function splitValuesError(drawnValue: string, restValue: string): string | null {
  if (!drawnValue.trim() || !restValue.trim()) return 'Escribe el estado de la parte dibujada y el del resto.';
  if (sameName(drawnValue, restValue)) return 'La parte dibujada y el resto tienen el mismo estado: no hay nada que dividir.';
  return null;
}

/**
 * Builds the whole split as values: (A) drawn ∩ active Suerte, (B) active Suerte − A, both as ordinary
 * OperationalSubareas, the Área parcial field (reused by name or created: Texto · Área parcial · suggested values),
 * and the two values. Throws on any invalid step; the inputs are never mutated.
 */
export function planSuerteSplit(input: SuerteSplitInput): SuerteSplitPlan {
  const { model, subareas } = input;
  const blocker = suerteSplitBlocker(model, input.suerteId, subareas); if (blocker) throw new Error(blocker);
  const valuesError = splitValuesError(input.drawnValue, input.restValue); if (valuesError) throw new Error(valuesError);
  const drawnValue = input.drawnValue.trim(); const restValue = input.restValue.trim();
  let fields = input.fields;
  let base: OperationalAttributeField | undefined;
  if ('fieldId' in input.base) {
    const fieldId = input.base.fieldId;
    base = fields.find(({ descriptor }) => descriptor.id === fieldId);
    if (!base || base.descriptor.level !== 'suerte' || base.descriptor.type !== 'string') throw new Error('Elige un dato de texto de nivel Suerte.');
  } else {
    base = createOperationalField(input.base.newName, 'string', 'suerte', fields, 'project', undefined, [drawnValue, restValue]);
    fields = [...fields, base];
  }
  const parent: SubareaParent = { level: 'suerte', id: input.suerteId };
  const drawnPreview = createSubareaPreview(model, parent, input.drawn);
  const drawnSubarea = createOperationalSubarea(drawnPreview, drawnValue, subareas, input.ids?.drawn);
  let restPreview: SubareaPreview;
  try { restPreview = createRemainingSubareaPreview(model, parent, [...subareas, drawnSubarea]); } catch {
    throw new Error(`La parte dibujada cubre toda la Suerte: no queda resto para «${restValue}». Dibuja solo la parte «${drawnValue}».`);
  }
  const restSubarea = createOperationalSubarea(restPreview, restValue, [...subareas, drawnSubarea], input.ids?.rest);
  const existingPartial = matchingPartialField(fields, base.descriptor.name);
  let partial = existingPartial;
  if (!partial) {
    const suggested = [...new Set([...splitValueSuggestions(fields, base.descriptor.id, base.descriptor.name), drawnValue, restValue])];
    partial = createOperationalField(base.descriptor.name, 'string', 'subarea', fields, 'project', undefined, suggested.length <= MAX_SUGGESTED ? suggested : [drawnValue, restValue]);
    fields = [...fields, partial];
  }
  const partialId = partial.descriptor.id;
  fields = fields.map((field) => field.descriptor.id !== partialId ? field : { ...field, valuesByEntityId: { ...field.valuesByEntityId, [drawnSubarea.id]: drawnValue, [restSubarea.id]: restValue } });
  return {
    parent, baseFieldId: base.descriptor.id, baseFieldName: base.descriptor.name, partialFieldId: partialId,
    createdBaseField: !('fieldId' in input.base), createdPartialField: !existingPartial,
    drawnValue, restValue, drawnPreview, restPreview, drawnSubarea, restSubarea,
    subareas: [...subareas, drawnSubarea, restSubarea], fields,
    basis: { model, subareas, fields: input.fields },
  };
}
